import { Injectable } from '@angular/core';
import { QueryEntity } from '@datorama/akita';
import { Observable } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';
import { NotificationsCenterMappingQuery } from '../notification-center-mappings/notifications-center-mapping.query';
import { NotificationCenterMessageView } from './models/notification-center-message-view.model';
import { NotificationCenterService } from './notification-center.service';
import { NotificationCenterStore, NotificationCenterState } from './notification-center.store';
import { getMessageLanguageForLcid } from './services/notification-center-helper.service';

@Injectable({ providedIn: 'root' })
export class NotificationCenterQuery extends QueryEntity<NotificationCenterState> {
  /** observable for the isOpen ui state */
  isOpen$ = this.select((state) => state.ui.isOpen);
  unseenNotificationsCount$ = this.select((state) => state.unseenCount);
  pageNum$ = this.select((state) => state.ui.currentPage);
  showPrev$ = this.select((state) => state.ui.currentPage !== 1);
  showNext$ = this.select((state) => state.ui.currentPage !== this.maxPage && this.maxPage > 1);
  public get pageSize(): number {
    return this.getValue().ui.amountPerPage;
  }

  public get maxPage(): number {
    return Math.ceil(this.getValue().total / this.pageSize);
  }

  /**
   * The amount we should offset the pagination by
   * e.g. page 2 with 10 items per page - offset should be 20
   */
  private _offset: number = 0;

  constructor(
    protected store: NotificationCenterStore,
    private mappingQuery: NotificationsCenterMappingQuery,
    private notificationService: NotificationCenterService
  ) {
    super(store);
  }

  /**
   * Select the paged view of the Notification Center Messages
   * @param lcid LCID for the messages that should be displayed
   */
  selectAllPagedView(lcid: number): Observable<NotificationCenterMessageView[]> {
    return this.pageNum$.pipe(
      switchMap((pageNum) => {
        this._offset = this.getOffset(pageNum);
        return this.selectAll().pipe(
          tap((notifications) => {
            // If we don't have the next page in the store retrieve it from the API
            // Only if max page > 0 (Notifications exist for user)
            if (notifications.length <= this._offset && this.maxPage > 0) {
              this.notificationService.getNotificationCenterPage(pageNum, this.pageSize).toPromise();
            }
          }),
          map((notifications) => notifications.slice(this._offset, this._offset + this.pageSize)),
          map((notifications) => {
            return notifications.map((n) => {
              let notificationView = n as unknown as NotificationCenterMessageView;

              // retrieve the linkUrl from the mappings and assign it
              let url = this.mappingQuery.getMappingFromNotifciation(notificationView);
              notificationView = Object.assign({}, notificationView, { linkUrl: url });

              if (n.messageLanguages) {
                let currentMessageLanguage = getMessageLanguageForLcid(n.messageLanguages, lcid);
                notificationView = Object.assign({}, notificationView, { messageLanguage: currentMessageLanguage });
              }

              return notificationView;
            });
          })
        );
      })
    );
  }

  /**
   * Get the ids of all unseen notifications in the store
   */
  getUnseenNotificationIdsFromStore(): number[] {
    return this.getAll({
      filterBy: (n) => !n.isRead,
    }).map((n) => n.id);
  }

  /**
   * Get Offset using page number supplied or current page
   * @param pageNum page number to use when calculating offset
   * @returns offset count of items (page 2 with a page size of 5 would be 10)
   */
  getOffset(pageNum?: number): number {
    if (!pageNum) {
      pageNum = this.getValue().ui.currentPage;
    }
    return (pageNum - 1) * this.pageSize;
  }
}
